import {
  FeatureFlag,
  useFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { m } from "@vega-core/paraglide/messages";

import {
  type ExportColumnConfig,
  type ExportGroupedHeader,
} from "../../../hooks/useTableExport";
import {
  formatAllocationAmountCellText,
  formatCommitmentAmountCellText,
  formatGpEstimatedTicketSizeMaxCellText,
  formatGpEstimatedTicketSizeMinCellText,
  formatGpIndicativeOrderCellText,
  formatIndicativeAmountCellText,
  formatLimitedPartnerCellText,
  formatLpTicketSizeMaxCellText,
  formatLpTicketSizeMinCellText,
  formatNdaCellText,
  formatProbabilityCellText,
  formatStageCellText,
  formatTierCellText,
  formatWeightedOrderCellText,
} from "../utils/formatCellContent";

type ColumnGroup = {
  groupLabel: string;
  columns: ExportColumnConfig<DealTrackingLimitedPartnerBreakdown>[];
};

type UseTrackingExportColumnsReturn = {
  columns: ExportColumnConfig<DealTrackingLimitedPartnerBreakdown>[];
  groupedHeaders: ExportGroupedHeader[];
};

export const useTrackingExportColumns = (): UseTrackingExportColumnsReturn => {
  const { featureFlag } = useFeatureFlag();
  const isBookBuildEnabled =
    featureFlag[FeatureFlag.COINVEST_BOOK_BUILD_VD_26153];

  const columnGroups: ColumnGroup[] = [
    {
      groupLabel: "",
      columns: [
        {
          header: m.coInvestments_tracking_limitedPartner(),
          accessor: formatLimitedPartnerCellText,
        },
        {
          header: m.coInvestments_tracking_stage(),
          accessor: formatStageCellText,
        },
        {
          header: m.coInvestments_tracking_tier(),
          accessor: formatTierCellText,
        },
        {
          header: m.coInvestments_tracking_nda(),
          accessor: formatNdaCellText,
        },
      ],
    },
    {
      groupLabel: m.coInvestments_tracking_lpGroup(),
      columns: [
        {
          header: m.coInvestments_tracking_lpMinimumTicketSize(),
          accessor: formatLpTicketSizeMinCellText,
        },
        {
          header: m.coInvestments_tracking_lpMaximumTicketSize(),
          accessor: formatLpTicketSizeMaxCellText,
        },
        {
          header: m.coInvestments_tracking_indicativeOrder(),
          accessor: formatIndicativeAmountCellText,
        },
      ],
    },
    {
      groupLabel: m.coInvestments_tracking_gpGroup(),
      columns: [
        {
          header: m.coInvestments_tracking_gpMinimumTicketSize(),
          accessor: formatGpEstimatedTicketSizeMinCellText,
        },
        {
          header: m.coInvestments_tracking_gpMaximumTicketSize(),
          accessor: formatGpEstimatedTicketSizeMaxCellText,
        },
        {
          header: m.coInvestments_tracking_gpIndicativeOrder(),
          accessor: formatGpIndicativeOrderCellText,
        },
        ...(isBookBuildEnabled
          ? [
              {
                header: m.coInvestments_tracking_gpProbability(),
                accessor: formatProbabilityCellText,
              },
              {
                header: m.coInvestments_tracking_gpWeightedOrder(),
                accessor: formatWeightedOrderCellText,
              },
            ]
          : []),
      ],
    },
    {
      groupLabel: "",
      columns: [
        {
          header: m.coInvestments_tracking_allocationTitle(),
          accessor: (row) =>
            formatAllocationAmountCellText(row, {
              forExport: true,
            }).join(""),
        },
        {
          header: m.coInvestments_tracking_commitmentAmount(),
          accessor: formatCommitmentAmountCellText,
        },
      ],
    },
  ];

  const columns = columnGroups.flatMap((group) => group.columns);
  const groupedHeaders = columnGroups.map((group) => ({
    label: group.groupLabel,
    colSpan: group.columns.length,
  }));

  return { columns, groupedHeaders };
};
